import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PhoneIncoming, Sparkles, X } from 'lucide-react';
import type { IncomingCallEvent } from '@4zen/api-client';
import { Badge, Button, ClientStatusBadge } from '@4zen/ui';
import { useRealtime } from '@/lib/realtime';
import { notify } from '@/lib/desktop';

/** The pop-up lingers only as long as a phone realistically rings. */
const DISMISS_AFTER_MS = 45_000;

/**
 * Screen-pop for inbound calls (§4): the client's card is one click away
 * before the operator picks up, and an unknown number is flagged as a fresh
 * lead so nobody greets a stranger like a regular.
 */
export function IncomingCallToast() {
  const navigate = useNavigate();
  const [call, setCall] = useState<IncomingCallEvent | null>(null);

  const handle = useCallback((frame: { event: string; payload: Record<string, unknown> }) => {
    if (frame.event !== 'call.incoming') return;
    const payload = frame.payload as unknown as IncomingCallEvent;
    if (payload.direction !== 'inbound') return;
    setCall(payload);
    void notify(
      payload.client_name ?? payload.from_number ?? 'Вхідний дзвінок',
      payload.is_new_client ? 'Новий контакт' : 'Вхідний дзвінок',
    );
  }, []);

  useRealtime(handle);

  useEffect(() => {
    if (!call) return;
    const timer = window.setTimeout(() => setCall(null), DISMISS_AFTER_MS);
    return () => window.clearTimeout(timer);
  }, [call]);

  if (!call) return null;

  const open = () => {
    if (call.deep_link) navigate(call.deep_link);
    setCall(null);
  };

  return (
    <div
      role="alert"
      className="fixed bottom-4 left-4 z-toast w-80 animate-slide-up rounded-lg border border-accent/40 bg-surface p-4 shadow-overlay"
    >
      <div className="flex items-start gap-3">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-accent-soft text-accent">
          <PhoneIncoming className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-xs uppercase tracking-wide text-content-subtle">Вхідний дзвінок</p>
          <p className="truncate font-medium">
            {call.client_name ?? call.from_number ?? 'Невідомий номер'}
          </p>
          {call.client_name && call.from_number ? (
            <p className="truncate text-xs text-content-subtle">{call.from_number}</p>
          ) : null}
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            {call.is_new_client ? (
              <Badge tone="warning">
                <Sparkles className="mr-1 h-3 w-3" />
                Новий контакт
              </Badge>
            ) : call.client_status ? (
              <ClientStatusBadge status={call.client_status as never} />
            ) : null}
          </div>
        </div>
        <button
          type="button"
          onClick={() => setCall(null)}
          aria-label="Сховати"
          className="text-content-subtle transition-colors hover:text-content"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      <Button className="mt-3 w-full" onClick={open} disabled={!call.deep_link}>
        {call.is_new_client ? 'Відкрити нову картку' : 'Відкрити картку'}
      </Button>
    </div>
  );
}
